import {
  Box,
  Button,
  Flex,
  HStack,
  SimpleGrid,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { FaRedo } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { Column } from 'react-table';
import {
  ChargeBatchItem,
  ChargeBatchItemStatus,
  findChargeBatchItems,
  getChargeBatch,
  retryChargeBatch,
} from 'services/api.service';
import TableComponent from 'components/TableComponent';
import { ChargeBatchStatusRender, ChargeBatchItemStatusRender } from '.';

export default function ChargesBatchItems() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const navigate = useNavigate();

  const { data: batch, refetch: refetchBatch } = useQuery(
    ['getChargeBatch', id],
    () => getChargeBatch(id),
  );

  const { mutate: retry, isLoading: retrying } = useMutation(
    () => retryChargeBatch(id),
    {
      onSuccess: (res) => {
        toast({
          title: 'Reprocessamento solicitado',
          description: `${res.requeued} item(s) reenfileirado(s).`,
          status: 'success',
          duration: 5000,
          position: 'top',
          isClosable: true,
        });
        refetchBatch();
        queryClient.invalidateQueries({ queryKey: ['findChargeBatchItems', id] });
        queryClient.invalidateQueries({ queryKey: ['findChargeBatches'] });
      },
      onError: () => {
        toast({
          title: 'Erro ao reprocessar',
          description: 'Não foi possível reprocessar as falhas.',
          status: 'error',
          duration: 5000,
          position: 'top',
          isClosable: true,
        });
      },
    },
  );

  const columns = useMemo(
    () =>
      [
        {
          accessor: 'externalId',
          Header: 'Item (externalId)',
        },
        {
          Header: 'Status',
          accessor: 'status',
          Cell: ({ value }: { value: ChargeBatchItemStatus }) => (
            <ChargeBatchItemStatusRender status={value} />
          ),
        },
        {
          Header: 'Cobrança',
          accessor: 'chargeId',
          Cell: ({ value }: { value: string | null }) =>
            value ? (
              <Button
                size="xs"
                variant="link"
                colorScheme="brand"
                onClick={() => navigate(`/access/charge/${value}`)}
              >
                {value}
              </Button>
            ) : (
              <Text color="gray.400" fontSize="xs">
                —
              </Text>
            ),
        },
        {
          Header: 'Tentativas',
          accessor: 'attempts',
        },
        {
          Header: 'Erro',
          accessor: 'error',
          Cell: ({ value }: { value: ChargeBatchItem['error'] }) => (
            <Text fontSize="xs" color="red.500">
              {value?.message ?? ''}
            </Text>
          ),
        },
        {
          Header: 'Ações',
          accessor: 'actions',
        },
      ] as Column[],
    [navigate],
  );

  const queryFn = (filter: { page: number; limit: number; query?: Record<string, any> }) =>
    findChargeBatchItems(id, {
      page: filter.page,
      limit: filter.limit,
      status: filter.query?.status as string | undefined,
    });

  return (
    <Box>
      <SimpleGrid mb="20px" columns={{ sm: 1 }} spacing={{ base: '20px', xl: '20px' }}>
        <Box>
          <HStack mb={4} justifyContent="space-between" flexWrap="wrap" spacing={4}>
            <Box>
              <HStack spacing={3} mb={1}>
                <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                  Lote #{id?.slice(0, 8)}
                </Text>
                {batch && <ChargeBatchStatusRender status={batch.status} />}
              </HStack>
              <Text color="gray.500" fontSize="sm">
                {batch?.createdAt
                  ? DateTime.fromISO(batch.createdAt).toFormat('dd/MM/yyyy HH:mm:ss')
                  : ''}
              </Text>
            </Box>
            <HStack spacing={4} flexWrap="wrap">
              {batch && (
                <Flex gap={6}>
                  <Box textAlign="center">
                    <Text fontSize="xs" color="gray.500">
                      Total
                    </Text>
                    <Text fontWeight="bold">{batch.totalItems}</Text>
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="xs" color="gray.500">
                      Sucesso
                    </Text>
                    <Text fontWeight="bold" color="green.500">
                      {batch.succeededItems}
                    </Text>
                  </Box>
                  <Box textAlign="center">
                    <Text fontSize="xs" color="gray.500">
                      Falhas
                    </Text>
                    <Text fontWeight="bold" color="red.500">
                      {batch.failedItems}
                    </Text>
                  </Box>
                </Flex>
              )}
              {batch && batch.failedItems > 0 && (
                <Button
                  leftIcon={<FaRedo />}
                  colorScheme="orange"
                  size="sm"
                  isLoading={retrying}
                  onClick={() => retry()}
                >
                  Reprocessar falhas ({batch.failedItems})
                </Button>
              )}
            </HStack>
          </HStack>

          <TableComponent
            label="Itens do Lote"
            columnsData={columns}
            queryFn={queryFn}
            queryKey={`findChargeBatchItems-${id}`}
            actions={() => null}
          />
        </Box>
      </SimpleGrid>
    </Box>
  );
}
